import { noop } from 'lodash';
import React, {
  Fragment,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { useAgent } from '~/contexts/AgentContext';
import { Actions } from '~/eds/src/components/Actions/Actions';
import { ActionsMenu } from '~/eds/src/components/ActionsMenu/ActionsMenu';
import { Box } from '~/eds/src/components/Box/Box';
import { Divider } from '~/eds/src/components/Divider/Divider';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { StatusText } from '~/eds/src/components/StatusText/StatusText';
import { Text } from '~/eds/src/components/Text/Text';
import { useToggle } from '~/eds/src/hooks/useToggle';
import { useUuid } from '~/eds/src/hooks/useUuid';
import { TextEditor } from '~/eds/src/modules/TextEditor/TextEditor';
import { BaseWelcomeContentProps } from '~/eds/src/types/BaseWelcomeContentProps';
import { ContentAst } from '~/eds/src/types/ContentAst';
import { IconType } from '~/eds/src/types/IconType';
import { MenuActions } from '~/eds/src/types/MenuActions';
import { Nullable } from '~/eds/src/types/Nullable';
import { SharedPanelProps } from '~/eds/src/types/SharedPanelProps';
import { User } from '~/eds/src/types/User';
import { UserAction } from '~/eds/src/types/UserAction';
import {
  parseContentAst,
  serializeContentAst,
} from '~/eds/src/utils/contentAst';
import { scrollElementIntoView } from '~/eds/src/utils/scrollElementIntoView';
import { typedMemo } from '~/eds/src/utils/typedMemo';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';

import type { AgentId } from '../chat-bot-panel/agentIds';
import { useWorkflowRoutingEnabled } from '../chat-bot-panel/hooks/useWorkflowRoutingEnabled';
import {
  ActiveContext,
  FeedbackDetails,
  MessageAction,
  Message as MessageType,
} from '../types/chat';
import { AIAgentMenu } from './AIAgentMenu';
import { AgentSelectorButton } from './components/AgentSelectorButton';
import { MESSAGES_CONTAINER_ID } from './constants';
import { Message as DeprecatedMessage } from './DEPRECATED_Message';
import {
  addAtSymbolToContent,
  removeAtSymbolFromContent,
  useAgentMenu,
} from './hooks/useAgentMenu';
import { Message as NewMessage } from './Message';

interface Props {
  disclaimer: {
    accept?: UserAction;
    welcomeContent?: BaseWelcomeContentProps;
    hasAccepted: boolean;
  };
  statusMessage?: {
    message: string;
    status?: 'danger' | 'warning' | 'info' | 'success' | 'active';
    title?: string;
    autoDismiss?: boolean;
  };
  interruptAction: UserAction;
  messages: MessageType[];
  otherUser: User;
  replyMessage: Nullable<MessageType>;
  title: string;
  messageActions: MessageAction[];
  footer?: React.ReactNode;
  /** This is the component that shows the active context above the TextEditor */
  activeContext?: ActiveContext;
  /** List of context actions in the active panel*/
  activeContextActions?: MenuActions<string>;
  /** A list of filter chips for current context swapping */
  context?: {
    actions: UserAction[];
    label: string;
  };
  disableInput?: boolean;
  /** if provided, will be rendered when `messages` array is empty */
  emptyConversation?: ReactElement;
  isLoadingReply?: boolean;
  isPosting?: boolean;
  isPrivate?: boolean;
  limit?: number;
  loadingContent?: SharedPanelProps['loadingContent'];
  moreActions?: UserAction[];
  placeholderContent?: SharedPanelProps['placeholderContent'];
  panelProps?: SharedPanelProps;
  thinkingEvents?: string[];
  chatActions?: UserAction[];
  userMessageActions?: ({
    resolveIcon?: (message: MessageType) => IconType;
  } & MessageAction)[];
  shouldDisableSources?: (message: MessageType) => boolean;
  onChoose: (message: MessageType, choice: string | any) => void;
  onPost: (text: string) => void;
  onSelectMessageSource: (message: MessageType, sourceIndex: number) => void;
  onSubmitFeedback: (
    message: MessageType,
    feedbackDetails: FeedbackDetails,
  ) => void;
  customMessageRenderer?: (
    message: MessageType,
    index: number,
    defaultProps: {
      disableChoices: boolean;
      messageActions: UserAction[];
      user: User;
      userMessageActions?: UserAction[];
      onChoose: (message: MessageType, choice: string | any) => void;
      onSelectSource: (message: MessageType, sourceIndex: number) => void;
      onSubmitFeedback: (
        message: MessageType,
        feedbackDetails: FeedbackDetails,
      ) => void;
      shouldDisableSources?: (message: MessageType) => boolean;
      shouldShowActions: boolean;
      thinkingEvents?: string[];
    },
  ) => React.ReactNode;
}

export const Chat = typedMemo(
  ({
    disclaimer,
    footer,
    messageActions,
    activeContext,
    context,
    disableInput,
    activeContextActions,
    emptyConversation,
    interruptAction,
    isLoadingReply = false,
    isPosting,
    isPrivate,
    limit,
    loadingContent,
    messages,
    moreActions,
    otherUser,
    panelProps,
    placeholderContent,
    replyMessage,
    statusMessage,
    title,
    thinkingEvents,
    chatActions,
    userMessageActions,
    shouldDisableSources,
    onChoose,
    onPost,
    onSelectMessageSource,
    onSubmitFeedback,
    customMessageRenderer,
  }: Props) => {
    const intl = useIntl();
    const agentContext = useAgent();
    const [isFocused, _toggleIsFocused, focus, blur] = useToggle();
    const currentUser = useCurrentUser();
    const isNewMessageFormatEnabled = useFlag(FlagType.AskAnything);
    const hasLanguageSelect = useFlag(FlagType.LanguageSelect);
    const isWorkflowRoutingEnabled = useWorkflowRoutingEnabled();
    const Message = isNewMessageFormatEnabled ? NewMessage : DeprecatedMessage;

    const [choice, setChoice] = useState('');
    const [choiceKey, updateChoiceKey] = useUuid();
    const [editorContent, setEditorContent] = useState<ContentAst>([]);
    const editorContainerRef = useRef<HTMLDivElement>(null);

    const {
      showAgentMenu,
      atSymbolPosition,
      searchQuery,
      openMenu,
      closeMenu,
      handleContentChange: handleAgentMenuContentChange,
      handleSelectAgent,
    } = useAgentMenu({
      editorContainerRef,
      onContentChange: (updatedContent) => {
        if (!choice) {
          setEditorContent(updatedContent);
        }
      },
    });

    const hasAcceptedDisclaimer = disclaimer.hasAccepted;

    const isLoading = loadingContent?.isLoading;
    const isReplying = Boolean(replyMessage);
    const handleInterrupt = interruptAction.onClick;

    // anytime a message is updated/added, always scroll to the end of the container
    useEffect(() => {
      scrollElementIntoView(MESSAGES_CONTAINER_ID, {
        behavior: 'smooth',
        block: 'end',
      });
      focus();
    }, [messages, replyMessage, thinkingEvents, isLoadingReply]);

    const handleChoose = useCallback(
      async (message: MessageType, choice: string | any) => {
        await blur();
        onChoose(message, choice);
        const choiceText =
          typeof choice === 'string' ? choice : choice?.workflowName || '';
        setChoice(choiceText);
        updateChoiceKey();
        focus();
      },
      [isFocused, onPost, onChoose],
    );

    const handlePost = useCallback(
      (updatedContent: ContentAst) => {
        onPost(serializeContentAst(updatedContent));
        setChoice('');
        setEditorContent([]);
        if (isWorkflowRoutingEnabled) {
          closeMenu();
        }
        updateChoiceKey();
        blur();
      },
      [onPost, closeMenu, isWorkflowRoutingEnabled],
    );

    const handleContentChange = useCallback(
      (updatedContent: ContentAst) => {
        if (!choice) {
          setEditorContent(updatedContent);
        }

        if (isWorkflowRoutingEnabled) {
          handleAgentMenuContentChange(updatedContent);
        }
      },
      [choice, handleAgentMenuContentChange, isWorkflowRoutingEnabled],
    );

    const isNewConversation = messages.length === 0;

    const handleSelectAgentWithContent = useCallback(
      (agentId: AgentId) => {
        const currentContent = choice ? parseContentAst(choice) : editorContent;

        handleSelectAgent(agentId, currentContent);
        if (choice) {
          const updatedContent = removeAtSymbolFromContent(currentContent);
          setChoice(serializeContentAst(updatedContent));
        }
      },
      [editorContent, choice, handleSelectAgent],
    );

    const handleAtButtonClick = useCallback(
      (position: { top: number; left: number }) => {
        const currentContent = choice ? parseContentAst(choice) : editorContent;
        const newContent = addAtSymbolToContent(currentContent);

        if (choice) {
          setChoice(serializeContentAst(newContent));
        } else {
          setEditorContent(newContent);
        }

        openMenu(position);
      },
      [editorContent, choice, openMenu],
    );

    const mapMessage = useCallback(
      (message: MessageType, index: number) => {
        if (customMessageRenderer) {
          const defaultProps = {
            disableChoices: isReplying,
            messageActions:
              message.userType === 'other'
                ? messageActions.map((messageAction) => ({
                    ...messageAction,
                    mode: 'icon' as const,
                    text: messageAction.tooltip,
                    level: isNewMessageFormatEnabled
                      ? ('subtle' as const)
                      : undefined,
                    onClick: () => messageAction.onClick(message, messages),
                  }))
                : [],
            user: message.userType === 'other' ? otherUser : currentUser,
            userMessageActions: userMessageActions?.map((action) => ({
              ...action,
              mode: 'icon' as const,
              icon: action.resolveIcon?.(message) ?? action.icon,
              text: action.tooltip,
              onClick: () => action.onClick(message, messages),
            })),
            onChoose: handleChoose,
            onSelectSource: onSelectMessageSource,
            onSubmitFeedback: onSubmitFeedback,
            shouldDisableSources: shouldDisableSources,
            shouldShowActions: index > 0,
            thinkingEvents: thinkingEvents,
          };
          const customRender = customMessageRenderer(
            message,
            index,
            defaultProps,
          );
          if (customRender) {
            return <Fragment key={message.id}>{customRender}</Fragment>;
          }
        }

        return (
          <Fragment key={message.id}>
            <Message
              disableChoices={isReplying}
              message={message}
              messageActions={
                message.userType === 'other'
                  ? messageActions.map((messageAction) => ({
                      ...messageAction,
                      mode: 'icon' as const,
                      text: messageAction.tooltip,
                      level: isNewMessageFormatEnabled ? 'subtle' : undefined,
                      onClick: () => messageAction.onClick(message, messages),
                    }))
                  : []
              }
              user={message.userType === 'other' ? otherUser : currentUser}
              onChoose={handleChoose}
              onSelectSource={onSelectMessageSource}
              onSubmitFeedback={onSubmitFeedback}
              shouldDisableSources={shouldDisableSources}
              shouldShowActions={index > 0}
              index={index}
              userMessageActions={userMessageActions?.map((action) => ({
                ...action,
                mode: 'icon' as const,
                icon: action.resolveIcon?.(message) ?? action.icon,
                text: action.tooltip,
                onClick: () => action.onClick(message, messages),
              }))}
            />
            {isNewMessageFormatEnabled && message.userType === 'other' && (
              <Divider />
            )}
          </Fragment>
        );
      },
      [
        messages,
        handleChoose,
        isReplying,
        onSelectMessageSource,
        shouldDisableSources,
        loadingContent,
        customMessageRenderer,
        messageActions,
        isNewMessageFormatEnabled,
        otherUser,
        currentUser,
        userMessageActions,
        onSubmitFeedback,
      ],
    );

    const messagesContent = useMemo(
      () => messages.map(mapMessage),
      [messages, userMessageActions, isReplying],
    );

    const getConversation = () => {
      if (isLoading) {
        return null;
      }

      const hasOnlyEmptyMessage =
        messages.length === 1 &&
        messages[0].text.trim() === '' &&
        messages[0].userType === 'other';
      if (emptyConversation && (!messages.length || hasOnlyEmptyMessage))
        return emptyConversation;
      return (
        <Layout
          direction="column"
          id={MESSAGES_CONTAINER_ID}
          justify="space-between"
          maxW="680px"
          mx="auto"
          my={0}
          overflowX="hidden"
        >
          {messagesContent}
          {replyMessage && (
            <Message
              isLoading={isLoadingReply}
              isReplying={isReplying}
              thinkingEvents={thinkingEvents}
              message={replyMessage}
              user={otherUser}
              onChoose={handleChoose}
              onSelectSource={onSelectMessageSource}
              onSubmitFeedback={onSubmitFeedback}
              shouldDisableSources={shouldDisableSources}
            />
          )}
        </Layout>
      );
    };

    const renderActiveContext = ({
      onReset,
      icon,
      text,
      disableReset,
    }: ActiveContext) => (
      <Layout
        align="center"
        justify="space-between"
        w="100%"
        bg={onReset ? 'status.active.secondary' : undefined}
        styles={[
          {
            borderTopLeftRadius: 'ml',
            borderTopRightRadius: 'ml',
          },
        ]}
      >
        <Box w={onReset ? '92%' : '100%'} h="100%">
          <ActionsMenu
            trigger={
              <Box px={2} w="100%">
                <StatusText
                  iconSpacing="s"
                  icon={icon}
                  text={text}
                  shouldTruncate
                />
              </Box>
            }
            actions={activeContextActions ?? []}
            name="chat context"
            triggerWidth="100%"
          />
        </Box>

        {onReset && (
          <IconButton icon="x" onClick={onReset} disabled={disableReset} />
        )}
      </Layout>
    );

    const actionsThresshold = 3;
    const actions = hasLanguageSelect
      ? (context?.actions ?? []).slice(0, actionsThresshold)
      : (context?.actions ?? []);
    const menuActions = hasLanguageSelect
      ? (context?.actions ?? []).slice(actionsThresshold)
      : undefined;

    const panelFooter = {
      actions: undefined,
      children: hasAcceptedDisclaimer ? (
        <Layout direction="column" spacing={2} maxW="680px" mx="auto" my={0}>
          {statusMessage && (
            <StatusMessage
              {...statusMessage}
              {...(statusMessage.autoDismiss ? {} : { onDismiss: noop })}
            />
          )}
          <Layout align="center" spacing={1}>
            {context && (
              <>
                <Text variant="body" fontWeight="400">
                  {context.label}{' '}
                </Text>
                <Actions actions={actions} moreActions={menuActions} size="s" />
              </>
            )}
          </Layout>
          <Layout
            direction="column"
            position="relative"
            ref={editorContainerRef}
          >
            <Box position="relative" w="100%">
              <TextEditor
                key={choiceKey}
                DEPRECATED_enableImprovedPost
                content={
                  choice
                    ? parseContentAst(choice)
                    : editorContent.length > 0
                      ? editorContent
                      : undefined
                }
                disabled={isLoading || disableInput}
                limit={limit}
                limitMode="words"
                isFocused={isFocused}
                isPosting={isPosting}
                isReplying={isReplying}
                mode="text"
                onBlur={blur}
                onFocus={focus}
                onChange={(updatedContent) => {
                  handleContentChange(updatedContent);
                  if (choice) {
                    const text = serializeContentAst(updatedContent);
                    setChoice(text);
                  }
                }}
                onPost={handlePost}
                onInterrupt={handleInterrupt}
                placeholder={
                  isNewConversation
                    ? intl.formatMessage({
                        id: 'Chat.newConversationPlaceholder',
                        defaultMessage: 'Enter a prompt for ASK AI',
                        description: 'Placeholder text for new conversation',
                      })
                    : intl.formatMessage({
                        id: 'Chat.inputPlaceholder',
                        defaultMessage: 'Ask or search anything…',
                        description: 'Placeholder text for chat input',
                      })
                }
                actions={chatActions}
                header={
                  activeContext ? renderActiveContext(activeContext) : undefined
                }
              />
              <AgentSelectorButton
                onButtonClick={handleAtButtonClick}
                showMenu={showAgentMenu}
                onCloseMenu={closeMenu}
              />
            </Box>
          </Layout>
          {isWorkflowRoutingEnabled && showAgentMenu && atSymbolPosition && (
            <Box
              data-agent-menu
              position="fixed"
              sx={{
                top: `${atSymbolPosition.top}px`,
                left: `${atSymbolPosition.left}px`,
                zIndex: 9999,
                pointerEvents: 'auto',
              }}
            >
              <AIAgentMenu
                onSelectAgent={handleSelectAgentWithContent}
                onClose={closeMenu}
                selectedAgentId={agentContext?.selectedAgent ?? null}
                searchQuery={searchQuery}
              />
            </Box>
          )}
          {footer}
        </Layout>
      ) : null,
      enableBorder: false,
    };

    const icon = isPrivate
      ? {
          icon: 'lock' as const,
          tooltip: intl.formatMessage({
            id: 'Chat.privateChatTooltip',
            defaultMessage:
              'This chat is only visible to you and system administrators.',
            description: 'Tooltip explaining chat privacy',
          }),
        }
      : undefined;

    return (
      <Panel
        enableContentPadding={!hasAcceptedDisclaimer}
        footer={panelFooter}
        icon={icon}
        loadingContent={loadingContent}
        moreActions={moreActions}
        placeholderContent={placeholderContent}
        welcomeContent={
          hasAcceptedDisclaimer ? undefined : disclaimer.welcomeContent
        }
        title={title}
        {...panelProps}
      >
        {getConversation()}
      </Panel>
    );
  },
);
